import React, { useEffect, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import axios from "axios";
import { GET_TOKEN, GLOBAL_API } from "../../api/apiUrls";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LabelList,
} from "recharts";
import { formatDateToDDMMM, generateDatesFromStartOfMonth } from "../../static/hook";

const StatCard = ({
  title,
  value,
  filterCondition,
  setPageViews,
  setNewPath,
}) => {
  const handleCardClick = () => {
    const queryParams = new URLSearchParams(filterCondition).toString();
    setNewPath(`mailer?${queryParams}`);
    setPageViews("mailer");
  };
  return (
    <div className="col-md-2 mb-3">
      <div
        className="card border-0 shadow rounded"
        onClick={() => { setNewPath('mailer'); setPageViews("mailer") }}
      >
        <div className="card-body">
          <p className="text-center text-capitalize">{title}</p>
          <p className="h2 text-center">{value}</p>
        </div>
      </div>
    </div>
  );
};

function StudentMailer({
  serverURL,
  user,
  setShowSideOverlay,
  setPageViews,
  setNewPath,
}) {
  const [emailStats, setEmailStats] = useState({
    totalEmails: 0,
    pendingTracker: 0,
    trackerDone: 0,
    openTodo: 0,
    totalTodo: 0,
  });
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationWiseData, setLocationWiseData] = useState(null);
  const [dateWiseData, setDateWiseData] = useState(null);
  const [chartData, setChartData] = useState([]);

  const fetchBaseQuery = async (query) => {
    const res = await axios.post(GLOBAL_API, {
      mstr: query,
      tokenNumber: GET_TOKEN,
    });
    return res.data || [];
  };

  const fetchData = async () => {
    try {
      const query =
        "SELECT LocCode, locname FROM locmas WHERE LocCode NOT IN (15, 16, 17, 18, 19)";
      const locations = await fetchBaseQuery(query);
      setLocationOptions(locations);

      const emailData = await axios.all([
        axios.post(`${serverURL}/api/v1/email/get-count`),
        axios.post(`${serverURL}/api/v1/email/get-count`, {
          actionable: { $in: ["pending", ""] },
        }),
        axios.post(`${serverURL}/api/v1/email/get-count`, {
          actionable: { $in: ["no-action", "Actionable"] },
        }),
        axios.post(`${serverURL}/api/v1/email/get-count`, {
          actionable: { $in: ["Actionable"] },
          status: { $nin: ["Close"] },
        }),
        axios.post(`${serverURL}/api/v1/email/get-count`, {
          actionable: { $in: ["Actionable"] },
        }),
      ]);

      setEmailStats({
        totalEmails: emailData[0]?.data || 0,
        pendingTracker: emailData[1]?.data || 0,
        trackerDone: emailData[2]?.data || 0,
        totalTodo: emailData[3]?.data || 0,
        openTodo: emailData[4]?.data || 0,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchDataLocationWise = async () => {
    try {
      const allData = {};

      if (locationOptions.length > 0) {
        const fetchLocationData = locationOptions.map(
          async ({ LOCCODE, LOCNAME }) => {
            const [
              totalMails,
              pendingTracker,
              trackerDone,
              totalTodo,
              openTodo,
              deadlineNotUpdate,
              followUpDate,
              overDue,
            ] = await Promise.all([
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["pending", ""] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["No Action", "Actionable"] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["Actionable"] },
                status: { $nin: ["Close"] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["Actionable"] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["Actionable"] },
                status: { $nin: ["Close"] },
                deadlineDate: { $in: [""] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-count`, {
                actionable: { $in: ["Actionable"] },
                status: { $nin: ["Close"] },
                followUpDate: { $in: [""] },
                locCode: { $in: [LOCCODE] },
              }),
              axios.post(`${serverURL}/api/v1/email/get-overdue-count`, {
                actionable: { $in: ["Actionable"] },
                status: { $nin: ["Close"] },
                locCode: { $in: [LOCCODE] },
              }),
            ]);

            allData[LOCNAME] = {
              totalMail: totalMails?.data || 0,
              pendingTracker: pendingTracker?.data || 0,
              trackerDone: trackerDone?.data || 0,
              totalTodo: totalTodo?.data || 0,
              openTodo: openTodo?.data || 0,
              deadlineNotUpdate: deadlineNotUpdate?.data || 0,
              followUpDate: followUpDate?.data || 0,
              overDue: overDue?.data || 0,
            };
          }
        );

        await Promise.all(fetchLocationData);
        setLocationWiseData(allData);
      }
    } catch (error) {
      console.error("Error in fetchDataLocationWise:", error);
    }
  };

  const fetchDataDateWise = async () => {
    try {
      const allData = {};
      const dateValues = generateDatesFromStartOfMonth();

      if (dateValues.length > 0) {
        const fetchDateCounts = await Promise.all(
          dateValues.map(async (date) => {
            const response = await axios.post(
              `${serverURL}/api/v1/email/get-count-by-date`,
              {
                date: date,
              }
            );
            allData[date] = response.data || 0;
          })
        );

        if (Object.keys(allData).length > 0) {
          setDateWiseData(allData);

          // Converting dateWiseData to chartData format
          const formattedData = Object.keys(allData).map((date) => ({
            name: formatDateToDDMMM(date),
            uv: allData[date], // Assuming 'uv' is the count value
          }));

          setChartData(formattedData);
        }
      }
    } catch (error) {
      console.error("Error in fetchDataDateWise:", error);
    }
  };
  useEffect(() => {
    fetchDataDateWise();
    fetchData();
  }, []);
  let data = [];
  useEffect(() => {
    if (locationOptions.length > 0) {
      fetchDataLocationWise();
    }
  }, [locationOptions]);

  const statFilters = {
    totalEmails: {},
    pendingTracker: { actionable: ["pending", ""] },
    trackerDone: { actionable: ["No Action", "Actionable"] },
    totalTodo: { actionable: ["Actionable"], status: { $nin: ["Close"] } },
    openTodo: { actionable: ["Actionable"] },
  };

  return (
    <div className="transback h-100 overflow-auto p-3 d-flex flex-column">
      <button
        className="btn text-secondary rounded-pill px-3 py-1 d-flex d-lg-none align-items-center gap-2"
        onClick={() => setShowSideOverlay(true)}
      >
        <MenuIcon />
      </button>
      <div className="headerUserDetails py-1 mb-3 d-flex justify-content-between">
        <span>YG Mail Dashboard</span>
        <div
          className="d-flex gap-2 align-items-center"
          title={`Name: ${user.name} | Email: ${user.email}`}
        >
          <i className="fa fa-user"></i>
          <span>{user.name}</span>
        </div>
      </div>

      <div className="w-100 h-100 overflow-auto">
        <div className="container-fluid mb-2">
          <div className="row justify-content-evenly">
            {Object.entries(emailStats).map(([key, value]) => (
              <StatCard
                key={key}
                title={key.replace(/([A-Z])/g, " $1").trim()}
                value={value}
                filterCondition={statFilters[key]}
                setPageViews={setPageViews}
                setNewPath={setNewPath}
              />
            ))}
          </div>
        </div>
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <table className="table">
                <thead>
                  <tr>
                    <td>Branch</td>
                    <td>Total Mails</td>
                    <td>Pending Tracker</td>
                    <td>Tracker Done</td>
                    <td>Total Todo</td>
                    <td>Open Todo</td>
                    <td>Deadline Not Updated</td>
                    <td>Followup Not Updated</td>
                    <td>Overdue To do</td>
                  </tr>
                </thead>
                <tbody>
                  {locationOptions?.map((item, idx) => (
                    <tr key={idx}>
                      <th>{item.LOCNAME}</th>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.totalMail || 0}
                      </td>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.pendingTracker || 0}
                      </td>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.trackerDone || 0}
                      </td>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.totalTodo || 0}
                      </td>
                      <td>{locationWiseData?.[item.LOCNAME]?.openTodo || 0}</td>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.deadlineNotUpdate ||
                          0}
                      </td>
                      <td>
                        {locationWiseData?.[item.LOCNAME]?.followUpDate || 0}
                      </td>
                      <td>{locationWiseData?.[item.LOCNAME]?.overDue || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg">
            <div style={{ width: "100%", aspectRatio: "16 / 5" }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  width={500}
                  height={400}
                  data={chartData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis dataKey={""} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="uv"
                    stroke="#8884d8"
                    fill="#8884d8"
                  >
                    <LabelList dataKey="uv" position="top" />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentMailer;
